import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    AirspaceAuthorisationService,
    AirspaceCheckCommand,
    ApproversMissionApprovalDto,
    DO_NOTHING,
    DisplayableMissionDto,
    FailedValidation,
    FieldError,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationFeatureDto,
    LookupObject,
    MissionCrewDetailsDto,
    MissionCrewDto,
    MissionService,
    NameValue,
    OperationAuthorisation,
    Organisation,
    PersonDto,
    PersonService,
    RequestAirspaceAuthorisationCommand,
    RpaDto,
    RpaTypeDto,
    RuleOutcome,
    SimpleAuthorityDto,
    UpdateMissionCommand,
    UserLocationService,
    UserService,
    WorkTracker,
    fromTimestamp,
    toLookup,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { FullScreenService } from '@flyfreely-portal-ui/fullscreen';
import {
    featureGroupMatchesPurpose,
    findLocationFeatureByPurpose,
    toMapFeature
} from '@flyfreely-portal-ui/locations';
import { FeatureGroup, MapSourceFilters } from '@flyfreely-portal-ui/map';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyJsonschema } from '@ngx-formly/core/json-schema';
import { Angulartics2 } from 'angulartics2';
import { Country } from 'libs/addresses/src/lib/address-edit/address-edit-dialogue.component';
import { EnhancedHelpService } from 'libs/enhanced-help/src/lib/enhanced-help.service';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import { RpaDialoguesService } from 'libs/rpa/src/lib/rpa-dialogues.service';
import CountryStates from 'libs/ui/src/lib/data/country-states.json';
import * as moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    BehaviorSubject,
    ReplaySubject,
    Subject,
    combineLatest,
    firstValueFrom,
    throwError
} from 'rxjs';
import { catchError, filter, startWith, take, takeUntil } from 'rxjs/operators';
import {
    AirspaceCheckService,
    findLocationFlightAreaPolygon
} from '../../airspace-check';
import { AirspaceDialoguesService } from '../../airspace-dialogues.service';
import { AirspaceAuthorisationManager } from '../airspace-authorisation-manager.service';
import { calculateAuthorisationListState } from '../authorisation-status';
import {
    buildFlightAreaGeometry,
    findFirstOperationError,
    findFlightAreaForOperatingArea,
    findSelectedFlightAreaPolygon,
    specialValidationKeys
} from '../helpers';
import {
    AirspaceAuthorisationState,
    CasaFlightProfiles,
    CraftWithError,
    TimeValidationResult
} from '../interfaces';
import {
    AirspaceAuthorisationForm,
    AirspaceAuthorisationFormValue,
    AirspaceAuthorisationPreviewService,
    airspaceAuthorisationFormErrors,
    authorisationTypeCanEditMissionInPreview,
    authorisationTypesAllowingMultipleFlightAreas,
    delegatedAuthorityLabel,
    justificationLabel,
    visibleAuthorisationFieldsByType
} from './airspace-authorisation-preview.service';
import {
    buildFormGroupFromFormlyFields,
    mapToFormlyConfig,
    removeWhiteSpace
} from './helpers';

const countryStates = CountryStates as Country[];

/**
 * Maps dot-notation field paths from `InvalidOperation.fieldErrors` to
 * human-readable messages shown in the authorisation preview footer.
 *
 * Keys are the raw field paths the server sends; values are the strings
 * displayed to the user when the field error has no `message` of its own.
 * Paths are matched with a prefix check so both exact keys (e.g.
 * `mission.maximumHeight`) and indexed variants (e.g.
 * `rpaType.performanceSpecifications.maxTakeOffWeight[0]`) are handled.
 */
const FIELD_ERROR_LABELS: Record<string, string> = {
    // Mission-level fields
    'mission.maximumHeight': 'Mission maximum height requires a value',
    'mission.missionDate': 'Mission start time is missing or invalid',
    'mission.missionEstimatedTime': 'Mission duration is missing or invalid',

    // Time fields surfaced directly by the authorisation service
    startTime: 'Mission start time is missing or invalid',
    endTime: 'Mission end time is missing or invalid',

    // Personnel
    rpic: 'Remote pilot details are invalid',
    remotePilot: 'Remote pilot details are invalid',
    remotePilotRegistrationDetails:
        'Remote pilot registration details are missing or invalid',
    remotePilotPhoneNumber: 'Remote pilot phone number is missing or invalid',
    rpicPhone: 'Remote pilot phone number is missing or invalid',
    rpicArn: 'Remote pilot licence number is missing or invalid',
    crp: 'Chief remote pilot details are invalid',

    // Aircraft / equipment
    rpaList: 'At least one valid RPA is required',

    // Authority
    delegatedAuthority: "The mission's delegated authority is invalid",

    // Airspace
    airspace: 'The airspace is not valid for this authorisation type',

    // Additional provider fields (NZL Airshare)
    descriptionOfOperatingArea:
        'A description of the operating area is required',
    operatorType: 'Operator type is required'
};

/**
 * Returns a human-readable label for a field error key.
 * Checks exact matches first, then falls back to a prefix match for array
 * or nested keys (e.g. `rpaType.performanceSpecifications.maxTakeOffWeight[2]`).
 */
function fieldErrorLabel(key: string, fe: FieldError): string {
    if (FIELD_ERROR_LABELS[key] != null) {
        return FIELD_ERROR_LABELS[key];
    }
    // Prefix match — covers indexed variants and deeper nesting
    const prefixMatch = Object.keys(FIELD_ERROR_LABELS).find(k =>
        key.startsWith(k)
    );
    if (prefixMatch != null) {
        return FIELD_ERROR_LABELS[prefixMatch];
    }
    // Fall back to server-supplied message, then code, then raw key
    return fe?.message ?? `${key}: ${(fe as any)?.code ?? String(fe)}`;
}

@Component({
    selector: 'airspace-authorisation-preview',
    templateUrl: './airspace-authorisation-preview.component.html',
    styleUrls: ['./styles.scss', '../../styles.scss'],
    providers: [AirspaceAuthorisationPreviewService]
})
export class AirspaceAuthorisationPreviewDialogue {
    @Input() organisation: Organisation;
    @Input() checkCommand: AirspaceCheckCommand;
    @Input() mission: DisplayableMissionDto;
    @Input() approval: ApproversMissionApprovalDto;
    // The authorisation to show
    @Input() authorisations: AirspaceAuthorisationDto[];
    @Input() rpaTypes: RpaTypeDto[];
    @Input() requestNew: boolean;

    @Output() missionUpdated = new EventEmitter<DisplayableMissionDto>();
    @Output() missionTimeUpdated = new EventEmitter<{
        startTime: Date;
        duration: number;
    }>();

    airspaceAuthorisationForm: FormGroup<AirspaceAuthorisationForm>;
    visibleAuthorisationFieldsByType = visibleAuthorisationFieldsByType;
    visibleFormFields: string[];

    airspaceAuthorisationType: AirspaceAuthorisationDto.AuthorisationType;
    airspaceAuthorisationStatus: AirspaceAuthorisationState;

    authorisationPreview: AirspaceAuthorisationResponse;
    authorisationResponse: AirspaceAuthorisationResponse;

    approvedIds: string[];

    rpic: PersonDto;
    crp: PersonDto;
    crpArn: string;
    crpAuthLabel: string;
    rpicArn: string;
    rpicAuthLabel: string;
    rpicPhone: string;

    rpaList: CraftWithError[];

    delegatedAuthority: SimpleAuthorityDto;

    acknowledgeCrp = false;
    acknowledgeWeight = false;

    features$ = new ReplaySubject<FeatureGroup[]>(1);
    locationFeatures: FeatureGroup[] = [];
    /**
     * All flight areas on the mission. For non-LAANC authorisation types
     * every one of these is always included in the request (as a
     * `MultiPolygon` when there's more than one — see
     * `flightAreaCommandGeometry`); there's no per-flight-area selection.
     * There is one `authorisationPreview.authorisationList` entry per flight
     * area (matched by geometric overlap with each entry's `operatingArea`,
     * see `flightAreaNameForAuthorisation`), shown in the
     * multi-authorisation summary table when there's more than one.
     */
    missionFlightAreas: LocationFeatureDto[];
    /** The single flight area selected from multiple flight areas via the pill selector. */
    selectedFlightArea: LocationFeatureDto;
    nextId = 0;

    missionEditorOpened = false;

    startTime: string;
    endTime: string;
    timeValidation: TimeValidationResult;
    civilTwilightStart: string;
    civilTwilightEnd: string;

    updatedTimeForm: FormGroup<{
        startTime: FormControl<Date>;
        duration: FormControl<number>;
    }>;
    editStart: boolean;
    editEnd: boolean;

    validationResultMessages: FailedValidation[];
    validRequest: boolean;
    canEditMission = false;

    // TODO: this error handler is getting convoluted and needs a refactor
    // We need a project-wide error handler that can handle dot notation field errors
    // at that point we can remove most of this or refactor it to use the dot notation better.
    missingRpicPhone: boolean;
    missingRpicJurisdictionIdentifier: boolean;
    missingRpicJurisdiction: boolean;
    rpicMissingRequiredRegistration = false;
    rpicInvalidRegistration = false;
    requestError = false;
    errorMessage: string;
    missingRpicMessages: {
        phone: string;
        jurisdictionIdentifier: string;
        jurisdiction: string;
        registration: string;
    } = {
        phone: 'Missing phone number',
        jurisdictionIdentifier: 'Missing licence number',
        jurisdiction:
            'Remote pilot does not have a licence in this jurisdiction',
        registration: 'Remote pilot must be registered'
    };
    craftMissingMtowIds: number[];
    rpicDetailsFields: FormlyFieldConfig[];
    rpicDetailsForm: FormGroup<{ phoneNumber: FormControl<string> }>;
    additionalInformationFormlyFields: FormlyFieldConfig[];
    additionalInformationForm: FormGroup;
    private additionalProviderProperties$ = new BehaviorSubject<any>(undefined);
    private additionalProviderRequiredKeys: string[] = [];

    casaFlightProfiles: NameValue[] = [
        { name: 'Automated - Grid', value: CasaFlightProfiles.AUTOMATED_GRID },
        {
            name: 'Automated - Waypoint',
            value: CasaFlightProfiles.AUTOMATED_WAYPOINT
        },
        { name: 'Manual', value: CasaFlightProfiles.MANUAL }
    ];
    casaFlightProfile: CasaFlightProfiles;

    collapseGcdTable: boolean;

    mapSourceFilters: MapSourceFilters = {};
    enabledMapLayerGroups: string[];

    operationFailed: boolean;
    authorisationServiceUnavailable = false;
    authorisationUnavailableMessage: string;

    expandWarningsAndBlockersIndex = 0;
    relatedMissionLookup: LookupObject<DisplayableMissionDto>;

    @ViewChild('map', { read: ElementRef, static: true }) map: ElementRef;

    private initValuesCompleted$ = new Subject<void>();

    authorisationLoading = false;
    working: boolean;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    private authorisationFormChanged$ = new Subject<void>();
    constructor(
        private airspaceAuthorisationService: AirspaceAuthorisationService,
        private airspaceAuthorisationManager: AirspaceAuthorisationManager,
        private airspaceCheckService: AirspaceCheckService,
        private airspaceAuthorisationPreviewService: AirspaceAuthorisationPreviewService,
        private personService: PersonService,
        private userService: UserService,
        private missionService: MissionService,
        private userLocationService: UserLocationService,
        private enhancedHelpService: EnhancedHelpService,
        private personnelDialoguesService: PersonnelDialoguesService,
        private missionDialoguesService: MissionDialoguesService,
        private rpaDialoguesService: RpaDialoguesService,
        private airspaceDialoguesService: AirspaceDialoguesService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService,
        private modal: BsModalRef,
        private fullScreenService: FullScreenService,
        private angulartics2: Angulartics2,
        private formlyJsonSchema: FormlyJsonschema
    ) {
        this.updatedTimeForm = new FormGroup({
            startTime: new FormControl<Date>(undefined),
            duration: new FormControl<number>(undefined)
        });

        this.rpicDetailsForm = new FormGroup({
            phoneNumber: new FormControl<string>(undefined)
        });
    }

    ngOnInit() {
        if (this.authorisations != null && this.authorisations.length > 0) {
            this.airspaceAuthorisationStatus = calculateAuthorisationListState(
                this.authorisations
            );
        }

        this.selectedFlightArea = findLocationFeatureByPurpose(
            this.mission?.location?.features,
            'FLIGHT_AREA'
        )[0];

        combineLatest([
            this.workTracker.observable.pipe(startWith(false)),
            this.airspaceAuthorisationManager.checkingAuthorisation$.pipe(
                startWith(false)
            ),
            this.airspaceCheckService.resultStatus$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking, status]) => {
                this.working =
                    working || serviceWorking || status === 'CHECKING';
                this.authorisationLoading = serviceWorking;
            });
        this.airspaceAuthorisationPreviewService.setInputData(
            this.mission,
            this.organisation
        );

        this.missionService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                if (!this.missionEditorOpened) {
                    this.refreshMission();
                }
            });

        this.personService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.setupValues());

        // Keep authorisation-type-dependent display state in sync.
        this.airspaceAuthorisationPreviewService.currentAuthorisationType$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(type => {
                this.airspaceAuthorisationType = type;
                this.canEditMission =
                    authorisationTypeCanEditMissionInPreview[type];
                if (type != null) {
                    this.visibleFormFields =
                        visibleAuthorisationFieldsByType[type];
                }
                // The map highlighting scheme differs for USA_LAANC (single
                // selected flight area) vs every other type (all flight
                // areas always active) — re-render now the type is known.
                this.renderMissionFlightAreasOnMap();
            });

        // React to the form being ready. The service only
        // emits authorisationFormReady$ *after* setupFormValues has run, so
        // the form is always fully populated when the component receives it.
        // combineLatest with initValuesCompleted$ ensures selectedFlightArea
        // is set (via setupValues) before checkAuthorisation is called.
        //
        // authorisationFormReady$ carries the authorisation type the form was
        // built for alongside the form itself, so airspaceAuthorisationType
        // (and the component variables it drives) is set from the same
        // synchronized emission that triggers checkAuthorisation().
        //
        // NOTE: distinctUntilChanged by reference is intentionally absent here.
        // The preview service reuses the same FormGroup instance across
        // refreshes (patchValue rather than recreate), so reference equality
        // never changes after the first emission and would permanently block
        // re-initialisation after a mission edit.  The authorisationFormChanged$
        // subject already tears down and recreates setupFormListeners on every
        // firing, making redundant firings harmless.
        combineLatest([
            this.initValuesCompleted$,
            this.airspaceAuthorisationPreviewService.authorisationFormReady$.pipe(
                filter(
                    (
                        v
                    ): v is {
                        form: FormGroup<AirspaceAuthorisationForm>;
                        type: AirspaceAuthorisationDto.AuthorisationType;
                    } => v?.form != null
                )
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([_, { form, type }]) => {
                this.airspaceAuthorisationForm = form;
                this.airspaceAuthorisationType = type;
                this.checkAuthorisation();
                this.authorisationFormChanged$.next();
                this.setupFormListeners();
            });

        // `authorisation.authorisationList` holds one entry per flight area
        // requested (in request order), shown in the multi-authorisation
        // summary table when there's more than one.
        this.airspaceAuthorisationManager.airspaceAuthorisationPreview$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(authorisation => {
                this.setupAuthorisationResult(authorisation);
            });
        this.airspaceAuthorisationManager.missionAirspaceAuthorisations$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(airspaceAuthorisation => {
                // Always show every current authorisation for the mission —
                // an authorisation type is no longer guaranteed to have at
                // most one entry, since multiple flight areas can each
                // produce their own concurrent authorisation.
                this.authorisations =
                    airspaceAuthorisation?.length > 0
                        ? airspaceAuthorisation
                        : null;
                this.airspaceAuthorisationStatus =
                    airspaceAuthorisation == null ||
                    airspaceAuthorisation.length === 0
                        ? 'UNKNOWN'
                        : // FIXME: this is a hack and needs to be revised
                          calculateAuthorisationListState(this.authorisations);
            });

        this.airspaceAuthorisationManager.authorisationServiceUnavailable$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(authorisationState => {
                this.authorisationServiceUnavailable =
                    authorisationState.status === 'UNAVAILABLE';
                this.authorisationUnavailableMessage =
                    authorisationState.unavailableMessage;
            });

        this.airspaceAuthorisationManager.relatedMissions$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(relatedMissions => {
                this.relatedMissionLookup = relatedMissions.reduce(
                    toLookup,
                    {}
                );
            });

        this.updatedTimeForm.controls.startTime.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(time => {
                this.updatedTimeForm.controls.startTime.setValue(time, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });

        this.updatedTimeForm.controls.duration.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(duration => {
                this.updatedTimeForm.controls.duration.setValue(duration, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });

        this.setupValues();

        // Fallback: when no authorisation types are available
        // This fallback ensures that, when types arrive as empty (or the
        // dialogue opens with existing authorisations and no types), we
        // still refresh the existing-authorisation list so the right panel
        // can be displayed.
        this.airspaceAuthorisationManager.availableAirspaceAuthorisations$
            .pipe(
                filter(types => types.length === 0),
                take(1),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(() => {
                if (
                    this.airspaceAuthorisationType != null &&
                    this.authorisations != null &&
                    this.authorisations.length > 0
                ) {
                    this.airspaceAuthorisationManager.refreshExistingMissionAuthorisations(
                        this.airspaceAuthorisationType
                    );
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.authorisationFormChanged$.next();
        this.authorisationFormChanged$.complete();
        this.features$.complete();
        this.initValuesCompleted$.complete();
    }

    refreshMission() {
        this.missionService
            .findMission(this.mission.id, this.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(mission => {
                this.resetComponentState();
                this.mission = mission;
                this.airspaceAuthorisationPreviewService.setInputData(
                    this.mission,
                    this.organisation
                );
                this.selectedFlightArea = findLocationFeatureByPurpose(
                    this.mission?.location?.features,
                    'FLIGHT_AREA'
                )[0];
                this.createCheckCommand(mission);
                this.airspaceAuthorisationManager.updateMission(mission);
                this.missionUpdated.emit(mission);
                this.setupValues();
            })
            .add(this.workTracker.createTracker());
    }

    setupFormListeners() {
        combineLatest([
            this.airspaceAuthorisationForm.valueChanges.pipe(
                startWith(this.airspaceAuthorisationForm.value)
            ),
            this.airspaceAuthorisationManager.authorisationStateErrors$
        ])
            .pipe(takeUntil(this.authorisationFormChanged$))
            .subscribe(([result, stateErrors]) => {
                this.errorMessage = null;
                // Get any state errors from the authorisation request
                const stateErrorText =
                    stateErrors != null
                        ? Object.keys(stateErrors)
                              .filter(
                                  key => !specialValidationKeys.includes(key)
                              )
                              .reduce((acc, k) => {
                                  if (stateErrors[k] == null) {
                                      return acc;
                                  }
                                  const fe = stateErrors[k] as FieldError;
                                  return acc.concat(fieldErrorLabel(k, fe));
                              }, [])
                        : [];
                // Add any generic error messages from invalid form fields,
                // but only when no HTTP-level state errors are present.
                // When stateErrors is non-null the preview call itself failed
                // (e.g. InvalidOperation), so form-control invalidity is noise
                // from validators running against an unpopulated form and would
                // be misleading to the user.
                const errorFields =
                    this.airspaceAuthorisationForm != null &&
                    stateErrors == null
                        ? stateErrorText.concat(
                              Object.keys(result ?? {}).reduce(
                                  (acc, key) =>
                                      this.airspaceAuthorisationForm.controls[
                                          key
                                      ].invalid
                                          ? acc.concat(
                                                airspaceAuthorisationFormErrors[
                                                    key
                                                ]
                                            )
                                          : acc,
                                  []
                              )
                          )
                        : stateErrorText;
                let message: string;
                // Update the current error message with any remaining error message.
                if (
                    result.authorisationErrors != null &&
                    Object.keys(result.authorisationErrors).length
                ) {
                    // Grab the first error in the errors object. We'll only display one at a time until all errors have been handled.
                    message = Object.keys(result.authorisationErrors)
                        .filter(key => !specialValidationKeys.includes(key))
                        .reduce(
                            (acc, k) =>
                                acc == null &&
                                result.authorisationErrors[k].message != null
                                    ? result.authorisationErrors[k].message
                                    : // FIXME: possible keys need to be better mapped out to show the displayed field heading in the error.
                                    result.authorisationErrors[k].code ===
                                      'NotNull'
                                    ? `field ${k} is missing a value`
                                    : acc,
                            null
                        );
                } else {
                    message =
                        errorFields.length > 0 ? errorFields.join(', ') : null;
                }
                this.errorMessage =
                    message == null || !Array.isArray(message)
                        ? message
                        : message[0].toUpperCase() + message.slice(1);

                // When more than one flight area is requested, call out
                // which one(s) currently have remaining errors so the
                // footer message isn't ambiguous about which area failed.
                const flightAreaSummary = this.buildFlightAreaErrorSummary();
                if (flightAreaSummary != null) {
                    this.errorMessage =
                        this.errorMessage != null
                            ? `${this.errorMessage}. ${flightAreaSummary}`
                            : flightAreaSummary;
                }
            });
    }

    /**
     * Reset all variables to their initial values so a clean refresh can happen.
     * This is probably overkill, but ensures nothing is skipped during a refresh.
     */
    resetComponentState() {
        // Clear the form reference so the old form is not rendered while a new
        // preview is being fetched.  Without this, the stale form would be
        // visible with `authorisationPreview == null` and
        // `authorisationLoading == false`, which is exactly the condition that
        // renders the "There are no automated authorisations available" banner.
        this.airspaceAuthorisationForm = null;
        this.authorisationPreview = null;
        this.rpicPhone = null;
        this.features$.next([]);
        this.locationFeatures = [];
        this.missionFlightAreas = null;
        this.selectedFlightArea = null;
        this.nextId = 0;
        this.startTime = null;
        this.endTime = null;
        this.timeValidation = null;
        this.civilTwilightStart = null;
        this.civilTwilightEnd = null;
        this.updatedTimeForm.reset();
        this.editStart = false;
        this.editEnd = false;
        this.validationResultMessages = null;
        this.validRequest = false;
        this.missingRpicPhone = false;
        this.missingRpicJurisdictionIdentifier = false;
        this.missingRpicJurisdiction = false;
        this.rpicMissingRequiredRegistration = false;
        this.rpicInvalidRegistration = false;
        this.requestError = false;
        this.errorMessage = null;
        this.missingRpicMessages = {
            phone: 'Missing phone number',
            jurisdictionIdentifier: 'Missing licence number',
            jurisdiction:
                'Remote pilot does not have a licence in this jurisdiction',
            registration: 'Remote pilot must be registered'
        };
        this.craftMissingMtowIds = null;
        this.rpicDetailsFields = null;
        this.rpicDetailsForm.reset();
        this.additionalInformationFormlyFields = null;
        this.additionalInformationForm = null;
        this.additionalProviderRequiredKeys = [];
        this.casaFlightProfiles = [
            {
                name: 'Automated - Grid',
                value: CasaFlightProfiles.AUTOMATED_GRID
            },
            {
                name: 'Automated - Waypoint',
                value: CasaFlightProfiles.AUTOMATED_WAYPOINT
            },
            { name: 'Manual', value: CasaFlightProfiles.MANUAL }
        ];
        this.casaFlightProfile = null;
        this.mapSourceFilters = {};
        this.enabledMapLayerGroups = null;
        this.operationFailed = false;
    }

    /**
     * The geometry to send as `RequestAirspaceAuthorisationCommand.flightArea`.
     * USA_LAANC keeps today's single-flight-area behaviour (the third-party
     * external provider is not assumed to support multiple flight areas);
     * every other authorisation type always sends every flight area on the
     * mission — a `Polygon` when there's only one, or a `MultiPolygon`
     * combining all of them when there's more than one.
     */
    private get flightAreaCommandGeometry() {
        return this.allowMultipleFlightAreas
            ? buildFlightAreaGeometry(this.missionFlightAreas)
            : findSelectedFlightAreaPolygon(this.selectedFlightArea);
    }

    createCheckCommand(mission: DisplayableMissionDto) {
        const startTime = mission.missionDate;
        const endTime =
            mission.missionEstimatedTime == null
                ? this.checkCommand.endTime
                : moment(startTime)
                      .add(mission.missionEstimatedTime, 'seconds')
                      .toISOString();

        const location =
            this.selectedFlightArea?.geometry ??
            findLocationFlightAreaPolygon(this.mission?.location?.features);

        const jurisdiction =
            mission.missionWorkflowVersion.delegatedAuthority.authorityType
                ?.jurisdiction?.identifier;

        const ruleset =
            mission.missionWorkflowVersion.delegatedAuthority.authorityType
                ?.ruleset?.identifier;

        const rpaList = mission.crafts
            .filter(
                r =>
                    r.rpaTypeId != null &&
                    this.rpaTypes[r.rpaTypeId]?.performanceSpecifications
                        ?.maxTakeOffWeight != null
            )
            .map(r => ({
                id: r.id,
                mtow: this.rpaTypes[r.rpaTypeId].performanceSpecifications
                    .maxTakeOffWeight,
                serialNumber: r.manufacturerSerialNumber
            }));

        this.checkCommand = {
            ...this.checkCommand,
            startTime,
            endTime,
            location,
            ruleset,
            jurisdiction,
            rpaList,
            nearestFeatureSearch: false,
            nearestFeatureSearchRadiusMeters: 400,
            maximumHeight: this.mission.maximumHeight ?? 400
        };
    }

    setupAirspaceCheckValidationMessages(reason: string) {
        const blocked =
            this.authorisationPreview.airspaceCheckResponse?.ruleOutcomes?.filter(
                ruleOutcome => ruleOutcome.outcome === RuleOutcome.Outcome.BLOCK
            ) ?? [];
        const messages = blocked.map(ruleOutcome => ruleOutcome.message);
        return [reason].concat(messages).join('<br>');
    }

    setupValues() {
        this.missionFlightAreas =
            this.mission.location != null
                ? findLocationFeatureByPurpose(
                      this.mission.location.features,
                      'FLIGHT_AREA'
                  )
                : null;
        if (this.missionFlightAreas != null) {
            this.selectedFlightArea = this.missionFlightAreas[0];
            this.renderMissionFlightAreasOnMap();
        }

        this.initValuesCompleted$.next();
    }

    /**
     * Renders every mission flight area on the map. USA_LAANC only ever
     * requests one flight area at a time (the single-select pill selector
     * driven by `selectFlightArea`), so the first one starts highlighted
     * bright and the rest dim; every other authorisation type always
     * requests every flight area on the mission at once (see
     * `flightAreaCommandGeometry`), so there's no single "selected" one —
     * they all render the same way. Safe to call again once the
     * authorisation type is known/changes: any previously rendered flight
     * area features are cleared first to avoid duplicates.
     */
    private renderMissionFlightAreasOnMap() {
        if (this.missionFlightAreas == null) {
            return;
        }
        this.locationFeatures = this.locationFeatures.filter(
            fg =>
                featureGroupMatchesPurpose(fg, 'FLIGHT_AREA') ||
                fg?.categories?.some(c => c.id === 'SECONDARY_FLIGHT_AREA')
        );
        const allActive = this.allowMultipleFlightAreas;
        this.missionFlightAreas.forEach((a, i) => {
            this.addFeature({
                name: a.name,
                type:
                    allActive || i === 0
                        ? LocationFeatureDto.Type.FLIGHT_AREA
                        : 'SECONDARY_FLIGHT_AREA',
                geometry: findLocationFlightAreaPolygon([a])
            } as LocationFeatureDto);
        });
    }

    setupExistingAuthorisation(fetchNew = true) {
        this.approvedIds = this.authorisations.map(
            authorisation => authorisation.id
        );
        const rpic = this.formValues.rpic;
        const rpicPhone = this.formValues.rpicPhone ?? rpic?.phoneNumber;
        const command: RequestAirspaceAuthorisationCommand = {
            missionId: this.mission.id,
            managingOrganisationId: this.mission.organisationId,
            remotePilotPhoneNumber: removeWhiteSpace(rpicPhone),
            authorisationType: this.airspaceAuthorisationType,
            flightArea: this.flightAreaCommandGeometry
        };
        this.authorisations.map(authorisation => {
            const geometry = <GeoJSON.Polygon>authorisation.operatingArea;
            this.addFeature({
                name: 'Operating Area',
                type: LocationFeatureDto.Type.AREA_OF_INTEREST,
                geometry: geometry
            } as LocationFeatureDto);
            this.requestError = false;
        });

        if (fetchNew) {
            this.airspaceAuthorisationManager.refreshAuthorisationPreview(
                command,
                this.formValues
            );
        }
    }

    editAdditionalFields() {
        // Strip null/undefined values before passing the model to the formly
        // dialogue. Angular FormControl stores unset values as null, but
        // FormlyJsonschema validators (enum, type) only exempt undefined, so
        // passing null for an optional field immediately marks it invalid and
        // keeps the Formly dialogue save button disabled even though the user hasn't touched it.
        const dialogueModel = Object.entries(
            this.additionalInformationForm.value ?? {}
        ).reduce(
            (acc, [k, v]) =>
                v !== null && v !== undefined ? { ...acc, [k]: v } : acc,
            {} as Record<string, any>
        );

        this.commonDialoguesService
            .showFormlyDialogue(
                'Additional Information',
                'Save',
                true,
                true,
                this.additionalInformationFormlyFields,
                dialogueModel,
                data =>
                    new Promise<void>(resolve => {
                        if (data != null) {
                            this.additionalInformationForm.patchValue(data);
                            // refresh the authorisation preview to check for errors after additional information has been added
                            this.checkAuthorisation(true);
                        }
                        resolve();
                    })
            )
            .catch(DO_NOTHING);
    }

    checkAuthorisation(previewOnly = false) {
        // if requesting a new authorisation, don't load the old one.
        if (this.authorisations != null) {
            this.authorisations.map(authorisation => {
                const geometry = <GeoJSON.Polygon>authorisation.operatingArea;
                this.addFeature({
                    name: 'Operating Area',
                    type: LocationFeatureDto.Type.AREA_OF_INTEREST,
                    geometry: geometry
                } as LocationFeatureDto);
                this.requestError = false;
            });
            this.requestError = false;
        }
        const rpic = this.formValues.rpic;
        const rpicPhone = this.formValues.rpicPhone ?? rpic?.phoneNumber;

        const command: RequestAirspaceAuthorisationCommand = {
            missionId: this.mission.id,
            managingOrganisationId: this.mission.organisationId,
            remotePilotPhoneNumber: removeWhiteSpace(rpicPhone),
            authorisationType: this.airspaceAuthorisationType,
            flightArea: this.flightAreaCommandGeometry,
            additionalProviderProperties: this.additionalInformationForm?.value
        };
        this.delegatedAuthority =
            this.mission?.missionWorkflowVersion?.delegatedAuthority;
        this.missingRpicJurisdiction = false;
        this.missingRpicJurisdictionIdentifier = false;
        this.missingRpicPhone = false;
        this.operationFailed = false;
        this.requestError = false;

        this.airspaceAuthorisationManager.refreshAuthorisationPreview(
            command,
            this.formValues
        );

        if (!previewOnly) {
            this.airspaceAuthorisationManager.refreshExistingMissionAuthorisations(
                this.airspaceAuthorisationType
            );
        }
    }

    /**
     * Derives the map's airspace dataset filters/layer groups from the
     * preview's airspace check response.
     */
    private applyMapSourceFilters(result: AirspaceAuthorisationResponse) {
        if (result?.airspaceCheckResponse != null) {
            this.mapSourceFilters =
                result.airspaceCheckResponse.airspaceDatasetList?.reduce(
                    (acc, v) => ({
                        ...acc,
                        [v.identifier]: v.version
                    }),
                    {} as MapSourceFilters
                );
            this.enabledMapLayerGroups = Object.keys(this.mapSourceFilters);
        }
    }

    setupAuthorisationResult(result: AirspaceAuthorisationResponse) {
        this.authorisationPreview = result;
        if (result == null) {
            return;
        }
        this.applyMapSourceFilters(result);

        // Persist current form values before rebuilding so they survive the refresh.
        if (this.additionalInformationForm != null) {
            const nonNullFormValues = Object.entries(
                this.additionalInformationForm.value
            ).reduce(
                (acc, [k, v]) =>
                    v !== null && v !== undefined ? { ...acc, [k]: v } : acc,
                {} as Record<string, any>
            );
            this.additionalProviderProperties$.next({
                ...this.additionalProviderProperties$.getValue(),
                ...nonNullFormValues
            });
        }

        // FIXME: temporarily enforce the new fields to airshare only.
        // Ideally CASA and LAANC should also use the API driven fields eventually
        const isAirshare =
            result.authorisationType ===
            AirspaceAuthorisationResponse.AuthorisationType.NZL_AIRSHARE;

        // Single source of truth: build formly fields from the API schema.
        // Currently only enforced for NZL_AIRSHARE
        // FIXME: other jurisdictions should also use this format instead of hard-coding
        this.additionalProviderRequiredKeys = isAirshare
            ? result?.additionalProviderProperties?.required ?? []
            : [];
        this.additionalInformationFormlyFields =
            isAirshare && result?.additionalProviderProperties != null
                ? mapToFormlyConfig(
                      this.formlyJsonSchema.toFieldConfig(
                          result.additionalProviderProperties
                      ).fieldGroup
                  )
                : null;

        // Rebuild form once — correct validators and restored values applied inside
        this.setupAdditionalInformationForm();

        // Apply NZL_AIRSHARE defaults only for fields not already set by the user
        if (
            result.authorisationType ===
            AirspaceAuthorisationResponse.AuthorisationType.NZL_AIRSHARE
        ) {
            this.additionalInformationForm.patchValue(
                {
                    descriptionOfOperatingArea:
                        this.additionalInformationForm.value
                            ?.descriptionOfOperatingArea ??
                        this.mission?.locationName ??
                        this.mission?.location?.name ??
                        '',
                    // FIXME: this needs to be better derived
                    operatorType:
                        this.additionalInformationForm.value?.operatorType ??
                        (this.delegatedAuthority?.authorityType?.ruleset?.abbreviation?.includes(
                            '102'
                        ) &&
                        this.delegatedAuthority?.authorityType?.activityType ===
                            'COMMERCIAL'
                            ? 'COMMERCIAL'
                            : 'RECREATIONAL')
                },
                { emitEvent: false }
            );
            this.additionalInformationForm.markAsPristine();
        }

        this.setupFormlyFields();
        this.updatedTimeForm.patchValue({
            startTime: fromTimestamp(
                this.mission.missionDate,
                this.mission.timeZone
            ),
            duration: this.mission.missionEstimatedTime
        });
        this.updatedTimeForm.markAsPristine();
        this.setOperatingArea(result);
    }

    showRegisterPilot() {
        this.angulartics2.eventTrack.next({
            action: 'authorisation-show-register-pilot',
            properties: {
                category: 'airspace-authorisation'
            }
        });
        const modal =
            this.airspaceDialoguesService.showRemotePilotRegistrationDialogue(
                this.authorisationPreview?.authorisationType ??
                    this.authorisations[0]?.authorisationType,
                this.formValues.rpic.id,
                this.organisation.id,
                this.formValues.rpic.phoneNumber
            );

        modal.content.success$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(success => {
                if (success) {
                    this.rpicMissingRequiredRegistration = false;
                    this.checkAuthorisation();
                    this.refreshMission();
                }
            });
    }

    updateRpicPhoneNumber(newNumber: string) {
        if (newNumber === this.formValues.rpicPhone) {
            return;
        } else {
            const rpic = {
                ...this.formValues.rpic,
                phoneNumber: newNumber
            };
            this.airspaceAuthorisationPreviewService.updateRpicTemporaryPhoneNumber(
                newNumber
            );
            this.checkAuthorisation(true);
        }
    }

    setupFormlyFields() {
        this.rpicDetailsFields = [
            {
                key: 'phoneNumber',
                type: 'phonenumber',
                props: {
                    label: `RPIC Phone Number${
                        this.missingRpicPhone ? '' : ' (optional)'
                    }`,
                    required: this.missingRpicPhone ? true : false,
                    disabled: this.working
                }
            }
        ];

        // Enrich additionalInformationFormlyFields with help text descriptions
        // from the enhanced help service, if any are available for this screen.
        if (this.additionalInformationFormlyFields != null) {
            this.enhancedHelpService
                .findByScreen('airspace-authorisation-preview')
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: helpFields => {
                        this.additionalInformationFormlyFields =
                            this.additionalInformationFormlyFields?.map(
                                field => {
                                    const helpEntry = helpFields?.find(
                                        h =>
                                            h.component ===
                                            (field.key as string)
                                    );
                                    if (
                                        helpEntry == null ||
                                        helpEntry.helpText == null ||
                                        helpEntry.helpText.length === 0
                                    ) {
                                        return field;
                                    }
                                    return {
                                        ...field,
                                        props: {
                                            ...field.props,
                                            description: helpEntry.helpText
                                        }
                                    };
                                }
                            );
                    },
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error while fetching additional form details: ${error.message}`
                        )
                })
                .add(this.workTracker.createTracker());
        }

        this.findCountryCode();
    }

    setupAdditionalInformationForm() {
        if (this.additionalInformationFormlyFields == null) {
            this.additionalInformationForm = null;
            return;
        }
        const savedValues = this.additionalProviderProperties$.getValue();
        this.additionalInformationForm = buildFormGroupFromFormlyFields(
            this.additionalInformationFormlyFields,
            this.additionalProviderRequiredKeys,
            savedValues
        );
        this.additionalInformationForm.updateValueAndValidity();
    }

    findCountryCode() {
        this.userLocationService
            .findUserLocation()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: res => {
                    const country = countryStates.find(
                        c => c.iso === res.value
                    );
                    if (country == null) {
                        return;
                    }
                    const number =
                        this.rpicDetailsForm.controls.phoneNumber.value ?? '';
                    this.rpicDetailsForm.controls.phoneNumber.patchValue(
                        `+${country.phonePrefix}${
                            number[0] === '0' ? number.slice(1) : number
                        }`
                    );
                },
                error: DO_NOTHING
            })
            .add(this.workTracker.createTracker());
    }

    private setOperatingArea(
        result: AirspaceAuthorisationDto | AirspaceAuthorisationResponse
    ) {
        // const geometry =
        //     result != null ? <GeoJSON.Polygon>result.operatingArea : null;
        // this.addFeature({
        //     name: 'Operating Area',
        //     type: LocationFeatureDto.Type.AREA_OF_INTEREST,
        //     geometry: geometry
        // } as LocationFeatureDto);
    }

    /** Switch the single flight area being requested. */
    selectFlightArea(area: LocationFeatureDto) {
        const oldSelected = this.selectedFlightArea;
        this.selectedFlightArea = area;
        this.highlightFlightAreaOnMap(oldSelected, area);
        this.checkAuthorisation();
    }

    /**
     * Rebuilds the FLIGHT_AREA / SECONDARY_FLIGHT_AREA map
     * feature groups so `highlighted` is shown bright (the "selected" style)
     * and every other mission flight area is shown dim. Non-LAANC
     * authorisation types request every checked flight area at once, so
     * there's no single "active" one to highlight.
     */
    private highlightFlightAreaOnMap(
        oldHighlighted: LocationFeatureDto,
        highlighted: LocationFeatureDto
    ) {
        this.locationFeatures = this.locationFeatures.filter(
            fg =>
                fg.name !== oldHighlighted?.name &&
                fg.name !== highlighted?.name &&
                fg?.categories?.find(
                    c =>
                        (c.name === oldHighlighted?.name &&
                            c.id === oldHighlighted?.type) ||
                        (c.name === highlighted?.name &&
                            c.id === highlighted?.type)
                ) == null
        );
        const index = this.missionFlightAreas.findIndex(
            f => f.name === highlighted?.name && f.type === highlighted?.type
        );
        this.missionFlightAreas.forEach((a, i) => {
            this.addFeature({
                name: a.name,
                type:
                    i !== index
                        ? 'SECONDARY_FLIGHT_AREA'
                        : LocationFeatureDto.Type.FLIGHT_AREA,
                geometry: findLocationFlightAreaPolygon([a])
            } as LocationFeatureDto);
        });
    }

    /**
     * Builds a summary of which mission flight areas currently have
     * authorisation entries with remaining validation errors, so the footer
     * can indicate which flight area(s) triggered a failure. Returns null
     * when there's only one mission flight area (nothing to disambiguate) or
     * nothing is failing. Entries are matched to flight areas by geometric
     * overlap (see `flightAreaNameForAuthorisation`).
     */
    private buildFlightAreaErrorSummary(): string | null {
        const authorisationList = this.authorisationPreview?.authorisationList;
        if (
            !this.allowMultipleFlightAreas ||
            authorisationList == null ||
            this.missionFlightAreas == null ||
            this.missionFlightAreas.length <= 1
        ) {
            return null;
        }
        const failingAreaNames = Array.from(
            new Set(
                authorisationList
                    .filter(operation => {
                        const keys = Object.keys(
                            operation?.errors ?? {}
                        ).filter(key => !specialValidationKeys.includes(key));
                        return keys.length > 0;
                    })
                    .map(operation =>
                        this.flightAreaNameForAuthorisation(operation)
                    )
                    .filter(name => name != null && name !== '')
            )
        );
        if (failingAreaNames.length === 0) {
            return null;
        }
        return `Flight area${
            failingAreaNames.length > 1 ? 's' : ''
        } ${failingAreaNames.join(', ')} ${
            failingAreaNames.length > 1 ? 'have' : 'has'
        } errors that must be resolved`;
    }

    /**
     * The status text shown for a single row of the multi-authorisation
     * preview summary table (shown above "Mission Details" whenever the
     * preview contains more than one entry — either because multiple flight
     * areas were requested, or because a longer mission was split into
     * multiple authorisation windows). Returns 'OK' when the entry has no
     * blocking errors, otherwise its first error message.
     */
    authorisationRowStatus(operation: OperationAuthorisation): string {
        return (
            findFirstOperationError(operation, specialValidationKeys) ?? 'OK'
        );
    }

    /**
     * Whether `authorisationRowStatus` for this entry represents a failure,
     * used purely for styling the summary table row.
     */
    authorisationRowHasError(operation: OperationAuthorisation): boolean {
        return (
            findFirstOperationError(operation, specialValidationKeys) != null
        );
    }

    /**
     * The name of the flight area a multi-authorisation summary table row's
     * `operatingArea` belongs to. Matched by geometric overlap rather than
     * equality, since the returned operating area won't necessarily match
     * the mission flight area's geometry exactly.
     */
    flightAreaNameForAuthorisation(operation: OperationAuthorisation): string {
        return (
            findFlightAreaForOperatingArea(
                operation?.operatingArea,
                this.missionFlightAreas ?? []
            )?.name ?? ''
        );
    }

    private addFeature(locationFeature: LocationFeatureDto) {
        if (
            this.locationFeatures.length === 0 ||
            this.locationFeatures.filter(
                fg =>
                    fg?.categories?.findIndex(
                        c => c.id === locationFeature.type
                    ) !== -1
            ).length === 0
        ) {
            this.locationFeatures = this.locationFeatures.concat({
                id: this.nextId,
                name: locationFeature.name,
                type: 'Polygon',
                canAdd: false,
                categories: [
                    {
                        id: locationFeature.type,
                        name: locationFeature.name
                    }
                ],
                existingFeatures:
                    locationFeature != null
                        ? [toMapFeature(locationFeature, this.nextId++)]
                        : [],
                styles: {
                    fill: [
                        {
                            paint: {
                                'fill-color': '#ff1234'
                            },
                            filter: ['!', ['has', 'category-id']]
                        },
                        {
                            paint: {
                                'fill-color': '#990000',
                                'fill-opacity': 0.4
                            },
                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.FLIGHT_AREA
                            ]
                        },
                        {
                            paint: {
                                'fill-color': '#6b3c3c',
                                'fill-opacity': 0.4
                            },
                            filter: [
                                '==',
                                ['get', 'category-id'],
                                'SECONDARY_FLIGHT_AREA'
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': 'no-fly',
                                'fill-opacity': 0.3
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.DANGER
                            ]
                        },
                        {
                            paint: {
                                'fill-pattern': 'danger-area',
                                'fill-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.NO_FLY
                            ]
                        },
                        {
                            paint: {
                                'fill-color': 'hsl(50,100,50)',
                                'fill-opacity': 0.4
                            },
                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.AREA_OF_INTEREST
                            ]
                        }
                    ],
                    line: [
                        {
                            paint: {
                                'line-color': '#990000',
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.FLIGHT_AREA
                            ]
                        },
                        {
                            paint: {
                                'line-color': '#6b3c3c',
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                'SECONDARY_FLIGHT_AREA'
                            ]
                        },
                        {
                            paint: {
                                'line-color': '#fecb1c',
                                'line-width': 3,
                                'line-opacity': 0.5
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.DANGER
                            ]
                        },
                        {
                            paint: {
                                'line-width': 3,
                                'line-opacity': 0.5
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.NO_FLY
                            ]
                        },
                        {
                            paint: {
                                'line-color': 'hsl(50,100,50)',
                                'line-width': 3,
                                'line-opacity': 1
                            },

                            filter: [
                                '==',
                                ['get', 'category-id'],
                                LocationFeatureDto.Type.AREA_OF_INTEREST
                            ]
                        }
                    ],
                    symbol: [
                        {
                            layout: {
                                'text-field': '{name}',
                                'symbol-placement': 'line',
                                'text-anchor': 'bottom',
                                'symbol-spacing': 150,
                                'text-max-angle': 35,
                                'text-size': 12,
                                'text-letter-spacing': 0.1,
                                'text-font': [
                                    'Roboto Bold',
                                    'Arial Unicode MS Regular'
                                ]
                            },
                            paint: {
                                'text-halo-color': 'hsl(0, 100%, 100%)',
                                'text-halo-width': 1,
                                'text-halo-blur': 1
                            }
                        }
                    ]
                }
            });
        } else {
            this.locationFeatures = this.locationFeatures.map(fg =>
                fg.categories.findIndex(c => c.id === locationFeature.type) !==
                -1
                    ? {
                          ...fg,
                          existingFeatures: fg.existingFeatures.concat(
                              toMapFeature(locationFeature, this.nextId++)
                          )
                      }
                    : fg
            );
        }
        this.features$.next(this.locationFeatures);
    }

    showPilot(pilot: PersonDto) {
        this.personnelDialoguesService.showPersonDetailsDialogue(
            pilot.id,
            this.mission.organisationId,
            false
        );
    }

    showRpa(rpa: RpaDto) {
        this.rpaDialoguesService.showCraftDetails(
            rpa.id,
            this.organisation.id,
            false
        );
    }

    cancelEditStart() {
        this.updatedTimeForm.controls.startTime.patchValue(
            fromTimestamp(this.mission.missionDate, this.mission.timeZone)
        );
        this.editStart = false;
    }

    cancelEditDuration() {
        this.updatedTimeForm.controls.duration.patchValue(
            this.mission.missionEstimatedTime
        );
        this.editEnd = false;
    }

    updateMissionTime() {
        const timeValues = <{ startTime: Date; duration: number }>(
            this.updatedTimeForm.value
        );
        this.missionTimeUpdated.emit(timeValues);
        if (this.canEditMission) {
            this.updateMissionDetails();
        }
        this.editStart = false;
        this.editEnd = false;
    }

    updateMissionDetails() {
        this.angulartics2.eventTrack.next({
            action: 'authorisation-mission-update',
            properties: {
                category: 'airspace-authorisation'
            }
        });

        const timeValues = this.updatedTimeForm.value;
        const updateCommand: UpdateMissionCommand = {
            ...this.mission,
            isDummy: this.mission.isDummy ?? false,
            equipmentIds: this.mission.equipment.map(e => e.id),
            missionCrew: this.mission.missionCrewDetails.reduce(
                (acc: MissionCrewDto[], c: MissionCrewDetailsDto) => [
                    ...acc,
                    {
                        missionRoleId: c.role.id,
                        personId: c.person.id
                    }
                ],
                []
            ),
            aerodromesOfInterest: this.mission.aerodromesOfInterest.map(
                a => a.identifier
            ),
            notams:
                this.mission.notams.map(n => ({
                    id: n.id,
                    location: n.location,
                    fir: n.fir
                })) ?? [],
            missionDate: toTimestamp(
                timeValues.startTime,
                this.mission.timeZone
            ),
            missionEstimatedTime: timeValues.duration,
            missionTypeId: this.mission.missionType.id,
            additionalAuthorityIds: this.mission.additionalAuthorities?.map(
                a => a.id
            ),
            emergencyContacts:
                this.mission.modelVersion === 1
                    ? this.mission.emergencyContacts
                    : null,
            radioFrequencies:
                this.mission.modelVersion === 1
                    ? this.mission.radioFrequencies
                    : null,
            missionObjectiveTypeId:
                this.mission.missionObjectiveType?.id ?? null
        };
        const doneWorking = this.workTracker.createTracker();
        this.missionService
            .updateMission(this.mission.id, updateCommand)
            .then(mission => {
                doneWorking();
                this.updatedTimeForm.markAsPristine();
                this.editStart = false;
                this.editEnd = false;
                this.authorisationPreview = null;
                this.mapSourceFilters = {};
                this.nextId = 0;
                this.resetComponentState();
                this.mission = mission;
                this.selectedFlightArea = findLocationFeatureByPurpose(
                    this.mission?.location?.features,
                    'FLIGHT_AREA'
                )[0];
                this.createCheckCommand(mission);
                this.airspaceAuthorisationManager.updateMission(mission);
                this.missionUpdated.emit(mission);
                this.setupValues();
            })
            .catch((error: FlyFreelyError) => {
                this.logging.error(error, error.message);
                doneWorking();
            });
    }

    goToRelatedMission(id: number) {
        this.airspaceAuthorisationManager.goToRelatedMission(id);
    }

    showMissionEditDialogue() {
        this.angulartics2.eventTrack.next({
            action: 'authorisation-mission-edit',
            properties: {
                category: 'airspace-authorisation'
            }
        });

        this.missionEditorOpened = true;
        const modal = this.missionDialoguesService.showMissionEditor(
            this.mission,
            this.organisation
        );

        modal.onHide.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
            this.missionEditorOpened = false;
            this.refreshMission();
        });
    }

    requestAuthorisation() {
        this.angulartics2.eventTrack.next({
            action: 'request-authorisation',
            properties: {
                category: 'airspace-authorisation'
            }
        });

        if (
            this.authorisationPreview.authorisationType ===
            'AUS_CASA_AUTHORISATION'
        ) {
            const dialogue =
                this.airspaceDialoguesService.showAuthorisationAcknowledgementDialogue();

            dialogue.content.acknowledged
                .pipe(takeUntil(this.ngUnsubscribe$), take(1))
                .subscribe(() => this.submit());
        } else {
            this.submit();
        }
    }

    submit() {
        const additionalInformation = this.additionalInformationForm?.value;
        const formValues = this.airspaceAuthorisationForm.value;
        // FIXME: this is still not the proper way to do this
        const additionalProperties =
            this.airspaceAuthorisationType ===
            AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION
                ? {
                      flightProfile:
                          this.airspaceAuthorisationForm.value.flightProfile
                  }
                : this.airspaceAuthorisationType ===
                  AirspaceAuthorisationDto.AuthorisationType.USA_LAANC
                ? {
                      safetyJustification: this.justificationRequired
                          ? this.airspaceAuthorisationForm.value
                                .safetyJustification
                          : null
                  }
                : additionalInformation?.flightPurpose != null &&
                  additionalInformation.flightPurpose.length > 0
                ? {
                      ...additionalInformation,
                      // TODO: figure out how to derive operatorType
                      operatorType:
                          additionalInformation.operatorType ?? 'RECREATIONAL'
                  }
                : null;
        const command: RequestAirspaceAuthorisationCommand = {
            missionId: this.mission.id,
            managingOrganisationId: this.mission.organisationId,
            remotePilotPhoneNumber: removeWhiteSpace(
                formValues.rpicPhone ?? formValues.rpic?.phoneNumber
            ),
            authorisationType: this.airspaceAuthorisationType,
            additionalProviderProperties: additionalProperties,
            flightArea: this.flightAreaCommandGeometry,
            publicMissionName: formValues.publicMissionName
        };
        this.requestError = false;

        this.airspaceAuthorisationManager
            .applyForAuthorisation(command, this.formValues)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.authorisationPreview = null;
                    // Always show every authorisation returned by the
                    // submission — an authorisation type is no longer
                    // guaranteed to have at most one entry, since multiple
                    // flight areas can each produce their own concurrent
                    // authorisation.
                    this.authorisations = result;
                    this.airspaceAuthorisationStatus =
                        calculateAuthorisationListState(this.authorisations);
                    this.setupExistingAuthorisation(false);
                    this.requestNew = false;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while requesting authorisation: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    cancelAuthorisation(authorisation: OperationAuthorisation) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                `Cancel Authorisation ${authorisation.id}`,
                'Are you sure you want to cancel this authorisation?',
                'Yes',
                () =>
                    firstValueFrom(
                        this.airspaceAuthorisationManager
                            .cancelAuthorisation(
                                authorisation.id,
                                this.airspaceAuthorisationType
                            )
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.logging.error(
                                        error,
                                        `Error while cancelling authorisation: ${error.message}`
                                    );
                                    return throwError(() => error);
                                })
                            )
                    )
            )
            .then(result => {
                doneWorking();
                this.logging.success('Authorisation cancelled successfully');
                this.checkAuthorisation();
            })
            .catch(() => doneWorking());
    }

    closeAuthorisation(authorisation: OperationAuthorisation) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                `Close Authorisation ${authorisation.id}`,
                'Are you sure you want to close this authorisation?',
                'Yes',
                () =>
                    firstValueFrom(
                        this.airspaceAuthorisationManager
                            .closeAuthorisation(
                                authorisation.id,
                                this.airspaceAuthorisationType
                            )
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.logging.error(
                                        error,
                                        `Error while closing authorisation: ${error.message}`
                                    );
                                    return throwError(() => error);
                                })
                            )
                    )
            )
            .then(result => {
                doneWorking();
                this.logging.success('Authorisation cancelled successfully');
                this.checkAuthorisation();
            })
            .catch(() => doneWorking());
    }

    deleteAuthorisation(authorisation: OperationAuthorisation) {
        const isFinalAuthorisation =
            this.authorisations.filter(a => a.id !== authorisation.id)
                .length === 0;
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                `Delete Authorisation`,
                'Are you sure you want to delete this authorisation? This cannot be undone.',
                'Yes',
                () =>
                    firstValueFrom(
                        this.airspaceAuthorisationManager
                            .deleteAuthorisation(
                                authorisation.id,
                                this.airspaceAuthorisationType
                            )
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.logging.error(
                                        error,
                                        `Error while deleting authorisation: ${error.message}`
                                    );
                                    return throwError(() => error);
                                })
                            )
                    )
            )
            .then(result => {
                doneWorking();
                this.logging.success('Authorisation deleted successfully');
                if (isFinalAuthorisation) {
                    this.close();
                } else {
                    this.checkAuthorisation();
                }
            })
            .catch(() => doneWorking());
    }

    acknowledgeRescinded(authorisation: OperationAuthorisation) {
        this.airspaceAuthorisationManager
            .acknowledgeRescinded(
                authorisation.id,
                this.airspaceAuthorisationType
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.logging.success('Rescinded status acknowledged');
                    this.checkAuthorisation();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while acknowledging rescinded status: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onFullscreenRequested() {
        this.fullScreenService.toggleFullScreen(this.map);
    }

    close() {
        this.modal.hide();
    }

    isFeet(unit: string) {
        return unit.toLowerCase() === 'feet';
    }

    getStatusCategory(
        status: AirspaceAuthorisationDto.Status
    ): 'PENDING' | 'TERMINATED' {
        switch (status) {
            case 'CANCELLED':
            case 'COMPLETED':
            case 'REJECTED':
                return 'TERMINATED';
            default:
                return 'PENDING';
        }
    }

    get isInPast() {
        return this.formValues?.startTime != null
            ? moment(this.formValues.startTime).isBefore(moment())
            : false;
    }

    get flightProfileName() {
        if (this.casaFlightProfile == null) {
            return '';
        }
        return this.casaFlightProfiles.find(
            p => p.value === this.casaFlightProfile
        )?.name;
    }

    get formValues() {
        return <AirspaceAuthorisationFormValue>(
            this.airspaceAuthorisationForm?.value
        );
    }

    get delegatedAuthorityLabel() {
        if (
            this.airspaceAuthorisationType == null ||
            delegatedAuthorityLabel[this.airspaceAuthorisationType] == null
        ) {
            return 'Delegated Authority';
        }
        return delegatedAuthorityLabel[this.airspaceAuthorisationType];
    }

    get justificationLabel() {
        if (
            this.airspaceAuthorisationType == null ||
            justificationLabel[this.airspaceAuthorisationType] == null
        ) {
            return 'Justification';
        }
        return justificationLabel[this.airspaceAuthorisationType];
    }

    get justificationRequired() {
        return this.airspaceAuthorisationPreviewService.justificationRequired;
    }

    /**
     * The first approved authorisation ID (singular convenience getter used by
     * the footer for display).  Previously referenced in the template as the
     * undefined `approvedId` property; this fixes that silent bug.
     */
    get approvedId(): string {
        return this.approvedIds?.[0] ?? '';
    }

    /**
     * Whether the current session can request a live authorisation preview.
     * True when there are available authorisation types for this mission.
     */
    get canRequestLivePreview(): boolean {
        return (
            this.airspaceAuthorisationType != null &&
            this.airspaceAuthorisationForm != null
        );
    }

    get allowMultipleFlightAreas(): boolean {
        return authorisationTypesAllowingMultipleFlightAreas[
            this.airspaceAuthorisationType
        ];
    }
}
